import * as cookie from 'js/lib/cookie';
import requestCountry from 'js/lib/requestCountry';

import {
  EMAIL_CONSTRAINT,
  FULLNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
  TOU_CHECKBOX_CONSTRAINT,
} from 'bundles/authentication-common/constants/constraints';
import localStorageEx from 'bundles/common/utils/localStorageEx';

declare const COURSERA_APP_NAME: string;

const EXCLUDED_APPS_FOR_LEGAL_CONSOLIDATION_AUTH_FLOW = [
  'front-page',
  'consumer-home',
  'authentication',
  'enterprise-home',
  'enterprise-description-page',
];

const isExcludedAppForLegalConsolidationAuthFlow = () => {
  const appName = COURSERA_APP_NAME;
  return EXCLUDED_APPS_FOR_LEGAL_CONSOLIDATION_AUTH_FLOW.includes(appName);
};

/**
 * Extract user email from _ue cookie and delete it immediately.
 * This is used to prefill the email field in the login form when the user is redirected with SSO error.
 *
 * @returns The user email from the cookie or undefined if the cookie is not found.
 */
export const getUserEmailFromCookie = (): string | undefined => {
  const cookieName = '_ue';
  const userEmail = cookie.get(cookieName);

  if (userEmail) {
    // Delete the cookie immediately after reading
    cookie.remove(cookieName);
    return userEmail;
  }

  return undefined;
};

// For automation tests like E2E, we need to bypass the bot manager flow, except for test cases that include Arkose.
export const shouldBypassBotManagerForAutomationTest = () =>
  localStorageEx.getItem('shouldBypassBotManager', Boolean, false);

// Utility function to determine when to show the terms of use acceptance flow this is for all US users
export const isTermsOfUseRolloutForUS = () => {
  return requestCountry.get() === 'US';
};

// Utility function to check if the legal consolidation auth flow should be shown to the user
export const shouldShowLegalConsolidationAuthFlow = (isUVRegisteredOnboardingSignupFlow = false) => {
  return (
    requestCountry.get() === 'US' &&
    (!isExcludedAppForLegalConsolidationAuthFlow() || isUVRegisteredOnboardingSignupFlow)
  );
};

// Utility function to check if the checkbox terms of use should be shown in the signup form
export const shouldShowCheckboxTermsOfUse = () => {
  return !shouldShowLegalConsolidationAuthFlow() && isTermsOfUseRolloutForUS();
};

export const shouldShowTOSDisclaimer = () => !shouldShowCheckboxTermsOfUse() && !shouldShowLegalConsolidationAuthFlow();

/**
 * Returns validation constraints for the signup form based on whether it's an enterprise SSO login.
 * Each constraint is a function that takes a string as an argument and returns a string
 * (indicating an error message) or a boolean (indicating that the input is valid).
 *
 * @param isEnterpriseSsoLogin - Whether this is an enterprise SSO login
 * @param showCheckboxTermsOfUse - Whether to show and validate the terms of use checkbox
 * @returns {Record<string, (arg: string) => string | false>} The validation constraints
 */
export const getSignupFormValidationConstraints = (
  isEnterpriseSsoLogin: boolean,
  showCheckboxTermsOfUse: boolean
): Record<string, (value: string) => string | false> => {
  const enterpriseValidationConstraints = {
    email: EMAIL_CONSTRAINT,
  };

  return isEnterpriseSsoLogin
    ? enterpriseValidationConstraints
    : {
        name: FULLNAME_CONSTRAINT,
        email: EMAIL_CONSTRAINT,
        password: PASSWORD_CONSTRAINT,
        touCheckbox: showCheckboxTermsOfUse ? TOU_CHECKBOX_CONSTRAINT : (_value: string) => false,
      };
};
